import type { HomeStateData } from '@workspace/api-backend'
import type { ReactNode } from 'react'
import { PROFILE_COMPLETE_THRESHOLD } from '../../../outreach/config/launch-checklist'
import { toneForState } from '../../config/rail'
import { CampaignPickerVisual } from './campaign-picker-visual'
import { DiagnosticVisual } from './diagnostic-visual'
import { LiveVisual } from './live-visual'
import { PeopleVisual } from './people-visual'
import { ProfileVisual } from './profile-visual'
import { ProgressVisual } from './progress-visual'
import { StatusVisual } from './status-visual'
import type { DiagnosticRow } from './types'

/**
 * Turn a resolved home state into its card's visual.
 *
 * This is the only place the wire payload meets the view layer. Everything
 * below it takes plain values, which is what keeps the visuals renderable from
 * fixtures — the states worth reviewing most (stall, drained, add-funds) are
 * the ones hardest to reproduce against a real account.
 *
 * Returns null for states whose card is carried entirely by its copy and CTA.
 */
export function renderHomeVisual(data: HomeStateData): ReactNode {
  const queued = data.queuedTotal
  // The panel inside the card always matches the card around it. Deriving it
  // from the same function the card uses means a state can never be amber
  // outside and red inside.
  const tone = toneForState(data.key)

  switch (data.key) {
    case 'QUEST_READING_DECK':
      return (
        <ProgressVisual
          waitingOn="Reading your deck the way an investor would."
          // Measured, not guessed: the read sits around five minutes at both
          // the median and the 90th percentile.
          eta="Usually about five minutes. You can leave this page and come back."
          rows={[
            { tone: 'ok', text: 'Deck received' },
            {
              tone: 'neutral',
              text: 'Building your feedback: strengths, gaps, and the questions investors will push on',
            },
          ]}
        />
      )

    case 'QUEST_FINDING_INVESTORS':
      return (
        <ProgressVisual
          waitingOn="Matching your profile against investors by thesis, stage, sector and check size."
          eta="Usually about a minute."
        />
      )

    case 'QUEST_PREPARING_LINK':
      return (
        <ProgressVisual
          waitingOn="Preparing your connection sign-in link."
          eta="Usually quick, sometimes a few minutes."
          rows={[
            {
              tone: 'neutral',
              text: 'Taking longer than usual? If it does not arrive, message us and a human will help.',
            },
          ]}
        />
      )

    case 'QUEST_COMPLETE_PROFILE':
      return (
        <ProfileVisual
          percent={data.profileCompletenessPercent}
          threshold={PROFILE_COMPLETE_THRESHOLD}
          nextSteps={data.profileNextSteps}
        />
      )

    case 'QUEST_READ_FAILED':
      return (
        <DiagnosticVisual
          tone={tone}
          header={{
            kind: 'callout',
            text: 'The read failed on our side. Your deck is fine, and our team was alerted automatically.',
          }}
          rows={[
            { tone: 'ok', text: 'Your deck is uploaded and safe' },
            { tone: 'ok', text: 'Retrying usually works right away' },
          ]}
        />
      )

    case 'QUEST_PAUSED':
      return (
        <StatusVisual
          statusTone="paused"
          statusLabel="Paused by you"
          statusDetail={data.lastPushAt ? `last sent ${formatDay(data.lastPushAt)}` : undefined}
          rows={[
            {
              tone: 'ok',
              text: `Your ${queued} approved messages are saved exactly where they stopped`,
            },
            { tone: 'ok', text: 'LinkedIn stays connected and healthy' },
            { tone: 'ok', text: 'Replies still arrive and still show here' },
          ]}
        />
      )

    case 'QUEST_RECONNECT':
      return (
        <StatusVisual
          statusTone="paused"
          statusLabel="Disconnected by you"
          statusDetail={
            data.disconnectRequestedAt
              ? `since ${formatDay(data.disconnectRequestedAt)}`
              : undefined
          }
          rows={[
            { tone: 'ok', text: `Your ${queued} approved messages are saved where they stopped` },
            { tone: 'ok', text: 'Your wallet and trial are unaffected' },
          ]}
        />
      )

    case 'ALERT_RECONNECT':
      return (
        <DiagnosticVisual
          tone={tone}
          header={{
            kind: 'status',
            tone: 'bad',
            label: 'Disconnected',
            detail: data.disconnectRequestedAt
              ? `since ${formatDay(data.disconnectRequestedAt)}`
              : undefined,
          }}
          rows={[
            {
              tone: 'stop',
              text: 'Your campaign stopped sending, and SeedForge can no longer track replies or outcomes. New replies land only in your LinkedIn inbox.',
            },
            {
              tone: 'ok',
              text: `Your ${queued} approved messages are saved and resume where they stopped`,
            },
            { tone: 'ok', text: 'Your wallet and trial are unaffected' },
          ]}
        />
      )

    case 'ALERT_SYSTEM_STALL':
      return (
        <DiagnosticVisual
          tone={tone}
          header={{
            kind: 'callout',
            text: 'Everything on your side is fine. Sending stopped on ours, and our team has been alerted.',
          }}
          // No cause rows: attributing a stall needs an error trail we do not
          // write yet, so the card stays honest and generic rather than guessing.
          rows={[
            { tone: 'ok', text: `Your ${queued} approved messages are safe and queued` },
            linkedInRow(data),
            {
              tone: 'ok',
              text: 'Your wallet and trial are fine. You are never charged while sending is down.',
            },
          ]}
        />
      )

    case 'ALERT_ADD_FUNDS': {
      // A topped-up founder paused on balance, not trial: count from the last
      // send. Under a day of silence is not worth a big "0".
      const silenceDays = data.addFundsAfterTopUp ? data.daysStalled : data.daysSinceTrialEnd
      return (
        <DiagnosticVisual
          tone={tone}
          header={{
            kind: 'status',
            // Paused for want of funds is a state, not a breakage — the same
            // dot the founder's own pause gets.
            tone: 'paused',
            label: 'Campaign paused',
            detail: data.lastPushAt ? `last message sent ${formatDay(data.lastPushAt)}` : undefined,
          }}
          bigNumber={
            silenceDays != null && silenceDays > 0
              ? {
                  value: String(silenceDays),
                  sentence: `days of silence · ${queued} of your ${data.matchCount} matched investors are waiting`,
                }
              : undefined
          }
          rowsHeading="What stopped it"
          rows={[
            {
              tone: 'todo',
              text: data.addFundsAfterTopUp
                ? 'Your wallet ran below the sending minimum, so sending paused. Add funds and it resumes from where it left off.'
                : 'Your free trial ended, so sending paused. Add funds and it resumes from where it left off.',
            },
            linkedInRow(data),
            { tone: 'ok', text: `${queued} contacts are approved and waiting to go` },
          ]}
        />
      )
    }

    case 'ALERT_QUEUE_DRAINED':
      return (
        <DiagnosticVisual
          tone={tone}
          header={{
            kind: 'callout',
            text: data.lastPushAt
              ? `The campaign is healthy. It has had nobody left to send to since ${formatDay(data.lastPushAt)}.`
              : 'The campaign is healthy. It has had nobody left to send to.',
          }}
          bigNumber={{
            // Zero is the definition of this state, not a reading — what varies
            // is how many matched investors are still waiting to be approved.
            value: '0',
            sentence: `approved contacts left · ${data.matchCount} matched investors are waiting on you`,
          }}
          rowsHeading="What stopped it"
          rows={drainedRows(data)}
        />
      )

    case 'QUEST_REVIEW_MESSAGES':
      return (
        <CampaignPickerVisual
          startupId={data.startupId}
          matchCount={data.matchCount}
          // The pool to choose from, not what is already approved — in this
          // state the latter is always zero.
          warmIntroCount={data.warmIntroAvailable}
          investorEnabled={data.investorTrack.enabled}
          warmIntroEnabled={data.warmIntroTrack.enabled}
        />
      )

    case 'QUEST_APPROVE_OUTREACH':
      return (
        <DiagnosticVisual
          tone={tone}
          header={{
            kind: 'callout',
            text: 'Your campaign is live and has nobody approved to send to yet.',
          }}
          bigNumber={{
            value: String(data.matchCount),
            sentence: 'matched investors are waiting on your approval',
          }}
          rows={[
            {
              tone: 'todo',
              text: 'Approve a batch. You can decline anyone, and repeat any time.',
            },
            linkedInRow(data),
            { tone: 'ok', text: 'Nothing sends until you approve the contact' },
          ]}
        />
      )

    case 'QUEST_LAUNCH_CAMPAIGN':
      // Same rule as the LinkedIn step: the count is only worth showing once
      // there is one, and "ready to send" is a claim about contacts, not setup.
      return (
        <StatusVisual
          statusTone="paused"
          statusLabel={queued > 0 ? 'Ready to send' : 'Not sending yet'}
          statusDetail={queued > 0 ? `${queued} approved` : undefined}
          rows={[
            { tone: 'ok', text: 'Your profile, messages and LinkedIn are set.' },
            { tone: 'todo', text: 'You approve each contact before anyone hears from you.' },
            { tone: 'ok', text: 'Your first month is free, so launching costs nothing.' },
          ]}
        />
      )

    case 'QUEST_CONNECT_LINKEDIN':
      // Two different situations wear this quest. With contacts approved the
      // campaign really is loaded and LinkedIn is the last thing missing. With
      // none approved it is not "ready" in any sense, and claiming the founder
      // approved contacts they have not even seen is the kind of thing that
      // makes them distrust every other number on the page.
      return queued > 0 ? (
        <StatusVisual
          statusTone="paused"
          statusLabel="Ready to send"
          statusDetail={`${queued} approved`}
          rows={[
            {
              tone: 'ok',
              text: 'You approved these contacts and messages. Nothing new sends without you.',
            },
            { tone: 'ok', text: 'Investors hear from you, from your own account.' },
            { tone: 'ok', text: 'Pause or disconnect at any time.' },
          ]}
        />
      ) : (
        <StatusVisual
          statusTone="paused"
          statusLabel="Not sending yet"
          rows={[
            { tone: 'todo', text: 'Connect LinkedIn so messages send from your own account.' },
            { tone: 'todo', text: 'Approve who we contact, so you see every name first.' },
            { tone: 'ok', text: 'Nothing sends until both are done.' },
          ]}
        />
      )

    case 'QUEST_LIVE':
      return (
        <LiveVisual
          sentTotal={data.sentTotal}
          queuedCount={queued}
          senderConnected={data.sender.connected}
          startupId={data.startupId}
          warmIntroAvailable={data.warmIntroAvailable}
          warmIntroEnabled={data.warmIntroTrack.enabled}
          investorEnabled={data.investorTrack.enabled}
          matchCount={data.matchCount}
          pausedTrackLabel={
            data.pausedTracks?.[0] === 'warm_intro'
              ? 'warm intro'
              : data.pausedTracks?.[0] === 'investor'
                ? 'investor'
                : undefined
          }
        />
      )

    case 'MOMENT_REPLY':
      return (
        <PeopleVisual
          people={data.replyPreviews}
          more={Math.max(0, data.replyCount - data.replyPreviews.length)}
        />
      )

    case 'MOMENT_OUTCOME_VERIFYING':
      return (
        <PeopleVisual
          people={[
            {
              name: data.outcomeInvestorName,
              fund: data.outcomeFundName,
              quote: data.outcomeSummary,
              kind: 'investor',
            },
          ]}
          footnote="Our team verifies this before anything is counted or charged. Nothing happens without a human review."
        />
      )

    case 'MOMENT_OUTCOME_CONFIRMED':
      return (
        <PeopleVisual
          people={[
            {
              name: data.outcomeInvestorName,
              fund: data.outcomeFundName,
              quote: data.outcomeSummary,
              kind: 'investor',
            },
          ]}
          footnote="Reviewed and verified by a person before it counted."
        />
      )

    default:
      // Upload deck only: its card carries the dropzone, passed in by the page.
      return null
  }
}

/**
 * The LinkedIn row, told from the payload rather than asserted.
 *
 * Four cards used to print "LinkedIn is connected and healthy" as a literal.
 * Two of them — the stall alert (reached via the ERROR short-circuit) and
 * add-funds (its own branch) — can render with no sender attached at all, so
 * the only cards a founder could see while disconnected were the ones telling
 * them the connection was fine.
 */
function linkedInRow(data: HomeStateData): DiagnosticRow {
  return data.sender.connected
    ? { tone: 'ok', text: 'LinkedIn is connected and healthy' }
    : {
        tone: 'todo',
        text: 'LinkedIn is not connected, so nothing can send until you reconnect it',
      }
}

function drainedRows(data: HomeStateData): DiagnosticRow[] {
  const track = data.drainedTracks?.[0] === 'warm_intro' ? 'warm intro' : 'investor'
  return [
    {
      // A repeatable step the founder owes the campaign, not a failure — the
      // card says so in words and must not contradict itself in colour.
      tone: 'todo',
      text: `Your approved ${track} contacts ran out. Approving is a step you repeat to keep the campaign fed.`,
    },
    linkedInRow(data),
    { tone: 'ok', text: 'Your wallet and trial are fine' },
  ]
}

const formatDay = (iso: string | Date): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
